import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CabiMascot from "../../components/Kids/CabiMascot";
import StatCard from "../../components/Kids/StatCard";
import Pill from "../../components/Kids/Pill";
import Button from "../../components/Kids/Button";

const API = process.env.REACT_APP_API_URL;

const SUBJECT_ICONS = { math: "🔢", english: "📖", science: "🔭", art: "🎨", coding: "💻", arabic: "🌍", general: "🎲" };
const LEVEL_COLORS  = ["bg-faint", "bg-success", "bg-blue-500", "bg-purple-500", "bg-gold", "bg-danger", "bg-pink-500"];

export default function ParentDashboard() {
  const { t }     = useTranslation();
  const navigate = useNavigate();
  const token    = localStorage.getItem("kids_parent_token");

  const [dash, setDash]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChild, setActiveChild] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingChild, setEditingChild] = useState(null);

  useEffect(() => {
    if (!token) { navigate("/kids/login"); return; }
    axios.get(`${API}/api/kids/parent/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const data = res.data.data;
        if (data.subscription?.is_expired) {
          toast.info("Your trial has ended. Choose a plan to keep learning! 🌟");
          navigate("/kids/parent/settings");
          return;
        }
        setDash(data);
        setLoading(false);
      })
      .catch(() => { toast.error("Session expired. Please login again."); navigate("/kids/login"); });
  }, []);

  const logout = () => {
    axios.post(`${API}/api/kids/parent/logout`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .finally(() => { localStorage.removeItem("kids_parent_token"); navigate("/kids/login"); });
  };

  if (loading) return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="text-center">
        <div className="text-6xl animate-bounce mb-4">⭐</div>
        <p className="text-brand font-display font-semibold">Loading your dashboard…</p>
      </div>
    </div>
  );

  const { subscription, children } = dash;
  const child = children[activeChild];

  return (
    <div className="min-h-screen font-body text-ink" style={{ background: "linear-gradient(180deg,#FFF4EC,#FFFBF7 280px)" }}>
      {/* Header */}
      <header className="bg-white px-5 sm:px-6 py-3.5 flex items-center justify-between sticky top-0 z-30 border-b border-cardline" style={{ backdropFilter: "blur(12px)" }}>
        <div className="flex items-center gap-2.5">
          <CabiMascot mood="happy" size={34} />
          <span className="font-display font-semibold text-[19px]">Cabil <span className="text-brand">Kids</span></span>
        </div>
        <div className="flex items-center gap-4">
          {subscription && (
            <div className={`text-xs font-body font-extrabold px-3 py-1 rounded-full ${
              subscription.status === "trialing" ? "text-[#8A6D0B]" : "bg-success/10 text-success"
            }`} style={subscription.status === "trialing" ? { background: "#FEF6D8" } : undefined}>
              {subscription.status === "trialing"
                ? `Trial: ${subscription.days_left} days left`
                : `${subscription.plan} — Active`}
            </div>
          )}

          {/* Account menu */}
          <div className="relative">
            <button onClick={() => setMenuOpen(o => !o)}
              className="w-10 h-10 rounded-full bg-orange-100 hover:bg-orange-200 flex items-center justify-center text-lg transition"
              aria-label="Account menu">
              👤
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute end-0 mt-2 w-52 bg-white rounded-card shadow-card border border-cardline py-2 z-20">
                  <button onClick={() => { setMenuOpen(false); navigate("/kids/parent/settings"); }}
                    className="w-full text-start px-4 py-2.5 text-sm font-body font-semibold text-ink hover:bg-orange-50 transition flex items-center gap-2">
                    ⚙️ {t("kids.dashboard.settings")}
                  </button>
                  <button onClick={() => { setMenuOpen(false); navigate("/kids/parent/settings"); }}
                    className="w-full text-start px-4 py-2.5 text-sm font-body font-semibold text-ink hover:bg-orange-50 transition flex items-center gap-2">
                    💳 {t("kids.dashboard.subscription")}
                  </button>
                  <button onClick={() => { setMenuOpen(false); navigate("/kids/parent/watch"); }}
                    className="w-full text-start px-4 py-2.5 text-sm font-body font-semibold text-ink hover:bg-orange-50 transition flex items-center gap-2">
                    📺 Watch rewards
                  </button>
                  <div className="border-t border-cardline my-1" />
                  <button onClick={() => { setMenuOpen(false); logout(); }}
                    className="w-full text-start px-4 py-2.5 text-sm font-body font-semibold text-danger hover:bg-red-50 transition flex items-center gap-2">
                    ↩️ {t("kids.dashboard.logout")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-[980px] mx-auto px-5 sm:px-6 py-8">

        <h1 className="font-display font-semibold text-[26px] sm:text-[28px] mb-1">{t("kids.dashboard.welcome")} 👋</h1>
        <p className="text-[15px] sm:text-base text-muted mb-6">Here's how your family is learning.</p>

        {/* Trial banner */}
        {subscription?.status === "trialing" && subscription.days_left <= 5 && (
          <div className="rounded-card p-4 mb-6 flex items-center justify-between gap-3" style={{ background: "#FEF6D8", border: "1px solid #F6DE97" }}>
            <div>
              <span className="font-body font-extrabold" style={{ color: "#8A6D0B" }}>⏰ Your trial ends in {subscription.days_left} days</span>
              <p className="text-sm mt-0.5" style={{ color: "#A98A2E" }}>Upgrade now to keep the learning going!</p>
            </div>
            <Link to="/kids/plans"
              className="font-body font-extrabold px-4 py-2.5 rounded-xl text-sm transition shrink-0" style={{ background: "#FBC531", color: "#2A2620" }}>
              Upgrade Plan
            </Link>
          </div>
        )}

        {/* Children selector */}
        {children.length > 0 ? (
          <>
            <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
              {children.map((c, i) => (
                <div key={c.id} className="flex flex-col items-center gap-1.5 shrink-0">
                  <Pill active={i === activeChild} onClick={() => setActiveChild(i)}>
                    <span className="text-base">🧒</span>
                    {c.name}
                    <span className="opacity-70">· {c.grade_label} · {t("kids.dashboard.level")} {c.level}</span>
                  </Pill>
                  <button onClick={(e) => { e.stopPropagation(); setEditingChild(c); }}
                    className="text-[10px] text-faint hover:text-brand font-body font-extrabold">
                    ✏️ Edit
                  </button>
                </div>
              ))}
              <Link to="/kids/parent/add-child"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 min-h-[44px] font-body font-extrabold text-[13px] border-2 border-dashed border-cardline text-faint hover:border-brand hover:text-brand transition self-center">
                <span className="text-base leading-none">＋</span> {t("kids.dashboard.addChild")}
              </Link>
            </div>

            {/* Active child stats */}
            {child && (
              <div className="grid md:grid-cols-4 gap-4 mb-8">
                <StatCard emoji="⭐" value={child.total_xp} label={`Total ${t("kids.dashboard.xp")}`} />
                <StatCard emoji="🎮" value={child.games_completed} label="Games Completed" />
                <StatCard emoji="🔥" value={child.streak_days} label={`Day ${t("kids.dashboard.streak")}`} />
                <StatCard emoji="⏱️" value={`${child.time_spent_mins}m`} label="Time Spent" />
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-card-lg p-12 text-center mb-8 shadow-card border border-cardline">
            <span className="text-6xl block mb-4">🧒</span>
            <h2 className="font-display font-semibold text-xl text-ink mb-2">Add your first child</h2>
            <p className="text-muted mb-6">{t("kids.dashboard.noChildren")}</p>
            <Button onClick={() => navigate("/kids/parent/add-child")}>
              {t("kids.dashboard.addChild")} 🌟
            </Button>
          </div>
        )}

        {/* Quick actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-card-lg p-5 text-white bg-gradient-to-br from-brand-light to-brand" style={{ boxShadow: "0 12px 28px rgba(238,95,43,.25)" }}>
            <div className="text-[32px] mb-2">🎮</div>
            <h3 className="font-display font-semibold text-[19px] mb-1">Play Games</h3>
            <p className="text-[13.5px] mb-3.5 text-white/85">Let your child start a learning game right now.</p>
            <Button onClick={() => navigate(child ? `/kids/games?childId=${child.id}` : "/kids/games")} className="!bg-white !text-brand !min-h-0 !py-2.5 !px-4 !text-[13px]">
              Open Games 🎮
            </Button>
          </div>
          <div className="rounded-card-lg p-5 bg-white border border-cardline shadow-card">
            <div className="text-[32px] mb-2">📊</div>
            <h3 className="font-display font-semibold text-[19px] mb-1 text-ink">Weekly Report</h3>
            <p className="text-[13.5px] mb-3.5 text-muted">See detailed progress, subjects covered, and time spent.</p>
            <Button variant="secondary" onClick={() => navigate("/kids/reports")} className="!min-h-0 !py-2.5 !px-4 !text-[13px] !border">
              View Report
            </Button>
          </div>
          <div className="rounded-card-lg p-5 bg-white border border-cardline shadow-card">
            <div className="text-[32px] mb-2">⭐</div>
            <h3 className="font-display font-semibold text-[19px] mb-1 text-ink">Chat with Cabil Star</h3>
            <p className="text-[13.5px] mb-3.5 text-muted">Your child's AI buddy is ready to help with anything.</p>
            <Button variant="secondary" onClick={() => navigate(child ? `/kids/ai-chat?childId=${child.id}` : "/kids/ai-chat")} className="!min-h-0 !py-2.5 !px-4 !text-[13px] !border">
              Start Chatting ⭐
            </Button>
          </div>
        </div>

        {/* Referral Section */}
        {dash.referral_code && (
          <div className="mt-6 rounded-card-lg p-6 shadow-card" style={{ background: "linear-gradient(90deg,#F6F2FF,#F0EBFF)", border: "1px solid #E4DAFB" }}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-start">
                <h3 className="font-display font-semibold text-lg text-navy">🎁 Refer Friends, Get Free Days!</h3>
                <p className="text-sm text-muted mt-1">Share your code with other parents. When they sign up, <b>you get 7 extra trial days</b> — for free!</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-white border-2 rounded-xl px-5 py-3 font-mono font-bold text-2xl tracking-widest shadow-card" style={{ borderColor: "#C9B8F5", color: "#7C5CFF" }}>
                  {dash.referral_code}
                </div>
                <button onClick={() => { navigator.clipboard.writeText(dash.referral_code); toast.success("Code copied!"); }}
                  className="text-white font-body font-extrabold px-4 py-3 rounded-xl transition text-sm" style={{ background: "#7C5CFF" }}>
                  Copy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {editingChild && <EditChildModal child={editingChild} token={token} onClose={() => setEditingChild(null)} onSaved={() => { setEditingChild(null); window.location.reload(); }} />}
    </div>
  );
}

function EditChildModal({ child, token, onClose, onSaved }) {
  const gradeNum = child.grade != null ? child.grade : (child.grade_label === "Kindergarten" ? 0 : parseInt(child.grade_label?.replace(/\D/g, "") || "1"));
  const [form, setForm] = useState({ name: child.name || "", nickname: child.nickname || "", grade: gradeNum });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/kids/parent/children/${child.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Child updated!");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || "Update failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-card-lg w-full max-w-md p-6 shadow-sheet" onClick={e => e.stopPropagation()}>
        <h2 className="font-display font-semibold text-lg text-ink mb-4">Edit {child.name}</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-body font-semibold text-muted mb-1">Name</label>
            <input value={form.name} onChange={e => set("name", e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-[#E5DED4] outline-none focus:border-brand text-ink font-body" />
          </div>
          <div>
            <label className="block text-sm font-body font-semibold text-muted mb-1">Nickname</label>
            <input value={form.nickname} onChange={e => set("nickname", e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-[#E5DED4] outline-none focus:border-brand text-ink font-body" />
          </div>
          <div>
            <label className="block text-sm font-body font-semibold text-muted mb-1">Grade</label>
            <select value={form.grade} onChange={e => set("grade", parseInt(e.target.value))}
              className="w-full px-3 py-2 rounded-xl border border-[#E5DED4] outline-none focus:border-brand text-ink font-body">
              <option value={0}>Kindergarten</option>
              {[1,2,3,4,5,6].map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-body font-semibold text-muted hover:bg-cream">Cancel</button>
          <Button onClick={save} disabled={saving} className="!min-h-0 !py-2 !px-5 !text-sm">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
